import { useCallback, useMemo, useState } from 'react';
import {
  ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  RowSelectionState,
  useReactTable,
} from '@tanstack/react-table';
import { DeviceTableToolbar } from '@/features/devices/components/device-tree-table/device-table-toolbar.tsx';
import { DeviceTableContent } from '@/features/devices/components/device-tree-table/device-table-content.tsx';
import { DeviceTablePagination } from '@/features/devices/components/device-tree-table/device-table-pagination.tsx';
import { TTreeDevice } from '@/features/devices/schemas/devices.schemas.ts';
import { useTranslation } from 'react-i18next';
import { getColumns } from './device-tree-table.columns.tsx';

export function DeviceTreeTable({
  data,
  setData,
  deviceInfo,
  isLoading,
}: {
  data: TTreeDevice[];
  setData?: (devices: TTreeDevice[]) => void;
  deviceInfo?: {
    plantId: string;
    timezone: string;
  };
  isLoading?: boolean;
}) {
  const [expanded, setExpandedRows] = useState<ExpandedState>({});
  const [rowSelection, setSelectedRows] = useState<RowSelectionState>({});
  const { t } = useTranslation();
  const isEditable = Boolean(setData);
  const columns = useMemo(() => getColumns({ isEditable, t }), [isEditable, t]);
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data,
    columns,
    state: {
      expanded,
      rowSelection,
    },
    initialState: {
      pagination: {
        pageSize: 20,
      },
    },
    autoResetPageIndex: false,
    enableRowSelection: true,
    onRowSelectionChange: setData && setSelectedRows,
    onExpandedChange: setExpandedRows,
    getSubRows: (row) => row.subDevices,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  const getSelectedTableRow = useCallback(() => {
    const allRows = table.getRowModel()?.rows;
    const selectedRowId = Object.keys(rowSelection)[0];
    return allRows.find((row) => row.id === selectedRowId);
  }, [table, rowSelection]);

  const getSelectedDevice = useCallback(() => {
    const selectedRowId = Object.keys(rowSelection)[0];
    if (!selectedRowId) return null;

    const row = table.getRow(selectedRowId);
    return row.original;
  }, [table, rowSelection]);

  const hasSelectedRow = Boolean(Object.keys(rowSelection)?.[0]);

  return (
    <div>
      <div className="w-full overflow-hidden rounded-md border">
        {setData && deviceInfo && (
          <DeviceTableToolbar
            data={data}
            deviceInfo={deviceInfo}
            hasSelectedRow={hasSelectedRow}
            getSelectedTableRow={getSelectedTableRow}
            getSelectedDevice={getSelectedDevice}
            setData={setData}
            setExpandedRows={setExpandedRows}
            setSelectedRows={setSelectedRows}
          />
        )}
        <DeviceTableContent
          table={table}
          columns={columns}
          rowSelection={rowSelection}
          setSelectedRows={setData && setSelectedRows}
          isLoading={isLoading}
        />
      </div>
      <DeviceTablePagination
        table={table}
        hasSelectedRow={hasSelectedRow}
        setSelectedRows={setData && setSelectedRows}
      />
    </div>
  );
}
